/**
 * Get tenant subdomain from URL
 * - Production: Extract from hostname (demo.fieldforce.app -> demo)
 * - Development/Pages.dev: Get from ?subdomain=xxx query param or subdomain in hostname
 */
export function getSubdomain(): string | null {
  const hostname = window.location.hostname;
  const searchParams = new URLSearchParams(window.location.search);

  // Check query param first (works everywhere)
  const querySubdomain = searchParams.get('subdomain');
  if (querySubdomain) {
    return querySubdomain;
  }

  // For pages.dev, check if there's a subdomain before the project name
  // e.g., demo.fieldforce-web.pages.dev -> demo
  if (hostname.endsWith('.pages.dev')) {
    const parts = hostname.split('.');
    // parts: ['demo', 'fieldforce-web', 'pages', 'dev'] = 4 parts
    // parts: ['fieldforce-web', 'pages', 'dev'] = 3 parts (no tenant subdomain)
    if (parts.length >= 4) {
      return parts[0];
    }
    // Default to 'demo' for testing if no subdomain specified
    return 'demo';
  }

  // Localhost without subdomain param
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'demo'; // Default to demo for local development
  }

  // Production: Extract subdomain from hostname
  const parts = hostname.split('.');

  // Need at least 3 parts (sub.domain.tld)
  if (parts.length < 3) {
    return null;
  }

  const subdomain = parts[0];

  // Skip www
  if (subdomain === 'www') {
    return null;
  }

  return subdomain;
}

/**
 * Check if we're in development mode
 */
export function isDevelopment(): boolean {
  const hostname = window.location.hostname;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.pages.dev')
  );
}

/**
 * Get current subdomain query param for navigation
 * Returns empty string if not in development
 */
export function getSubdomainParam(): string {
  const subdomain = getSubdomain();
  if (isDevelopment() && subdomain) {
    return `?subdomain=${subdomain}`;
  }
  return '';
}
